import * as action from '../../../../../state/actionConstants';
import { ActionTypes } from 'redux-undo';
import { IpexMomentEnum } from '@insights/insights-data-provider';
import { getInsiktReporter } from '../../../analytics';
import {
  sanitizeObject,
  findValueByKeyInObject,
  isNonInteractionEvent,
} from '../../../utils/object';
interface CustomReduxEvent {
  ipexMoment: IpexMomentEnum;
  event: string;
  payload?: any;
  keys?: Array<string> | undefined;
  reportEvent: Function;
}

interface CustomReduxEvents {
  [key: string]: CustomReduxEvent;
}

/**
 * Calls the insightsApi to report the custom event.
 * @param event
 * @param payload
 * @param meta
 */
const reportCustomReduxEvent = (
  actionType: string,
  payload: any,
  meta: any
) => {
  const eventToReport = buildCustomReduxStatisticsEvent(
    actionType,
    payload,
    meta
  );
  getInsiktReporter().reportCustomEvent(eventToReport);
};

export const customReduxEvents: CustomReduxEvents = {
  [action.SCENE_SHOW_MEASUREMENTS]: {
    ipexMoment: IpexMomentEnum.showMe,
    event: 'scene_show_measurements',
    payload: {},
    keys: ['nonInteraction'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.SCENE_HIDE_MEASUREMENTS]: {
    ipexMoment: IpexMomentEnum.showMe,
    event: 'scene_hide_measurements',
    payload: {},
    keys: ['nonInteraction'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.SCENE_SET_WALL_RESIZER_ACTIVE]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'scene_set_wall_resizer_active',
    payload: {},
    keys: ['nonInteraction'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.SCENE_SET_WALL_RESIZER_INACTIVE]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'scene_set_wall_resizer_inactive',
    payload: {},
    keys: ['nonInteraction'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.SCENE_ITEM_PICKED_UP]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'scene_item_picked_up',
    payload: {},
    keys: ['item', 'xPosition', 'yPosition', 'zPosition'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.TAC_SET_WALL]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'tac_set_wall',
    payload: {},
    keys: ['origin', 'height', 'width'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.TAC_SET_USE_MOUNTING_RAIL]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'tac_set_use_mounting_rails',
    payload: {},
    keys: ['enableMountingRails'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.PRODUCT_MENU_SET_FILTER]: {
    ipexMoment: IpexMomentEnum.whatIsMe,
    event: 'product_menu_set_filter',
    payload: {},
    keys: [
      'filter',
      'nonInteraction',
      'subFilterColor',
      'subFilterDepth',
      'nonInteraction',
    ],
    reportEvent: reportCustomReduxEvent,
  },
  [action.TAC_ADD_ITEM]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'tac_add_item',
    payload: {},
    keys: ['item', 'parent', 'connectsTo', 'origin'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.TAC_REMOVE_ITEM]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'tac_remove_item',
    payload: {},
    keys: ['item', 'optionType', 'origin'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.TAC_UPDATE_ITEM]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'tac_update_item',
    payload: {},
    keys: [
      'item',
      'parent',
      'connectsTo',
      'optionValue',
      'optionType',
      'origin',
      'xPosition',
      'yPosition',
      'zPosition',
    ],
    reportEvent: reportCustomReduxEvent,
  },
  [action.SET_VIEW]: {
    ipexMoment: IpexMomentEnum.showMe,
    event: 'navigation_set_view',
    payload: {},
    keys: ['view', 'nonInteraction'],
    reportEvent: reportCustomReduxEvent,
  },
  [action.DIALOG_OPEN]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: 'dialog_open',
    payload: {},
    keys: ['origin', 'dialogType'],
    reportEvent: reportCustomReduxEvent,
  },
  [ActionTypes.UNDO]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'scene_undo',
    payload: {},
    keys: [],
    reportEvent: reportCustomReduxEvent,
  },
  [ActionTypes.REDO]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: 'scene_redo',
    payload: {},
    keys: [],
    reportEvent: reportCustomReduxEvent,
  },
};

/**
 * Used by other files/modules to access the customReduxEvents object.
 * @param reduxEventType
 * @returns ICustomReduxEvent
 */
export const getReduxEvent = (reduxEventType: string) => {
  return customReduxEvents[reduxEventType];
};

/**
 * Builds the custom event by formatting it according to insights specifications.
 * Insights specification for events:
 * ipexMoment: 1/5 enum choices.
 * event: snake_case.
 * payload: single-depth object hierarchy with only primitives.
 * @param actionType
 * @param payload
 * @param meta
 * @returns {object}
 */
export const buildCustomReduxStatisticsEvent = (
  actionType: string,
  payload: any = {},
  meta: any = {}
) => {
  const sanitizedPayload = sanitizeObject(payload);
  const sanitizedMeta = sanitizeObject(meta);

  const customReduxEvent = customReduxEvents[actionType];

  const { keys, reportEvent, ...event } = {
    ...customReduxEvent,
    ipexMoment: isNonInteractionEvent(meta)
      ? IpexMomentEnum.nonInteraction
      : customReduxEvent.ipexMoment,
    payload: {
      ...buildPayloadBasedOnKeys(
        customReduxEvents[actionType].keys,
        sanitizedPayload,
        sanitizedMeta
      ),
    },
  };
  return event;
};

/**
 * Builds a payload object based on the keys defined in the customReduxEvents objects.
 * The keys are used to filtrate through all of the possible values we have "caught" in the reduxEventBuilder.js file.
 * @param keys
 * @param payload
 * @param meta
 * @returns {object}
 */
const buildPayloadBasedOnKeys = (
  keys: string[] | undefined,
  payload: any,
  meta: any
): any => {
  if (!keys) return;
  const builtPayload: any = {};
  keys.forEach((key: string) => {
    const value = findValueByKeyInObject(payload, key);
    builtPayload[key] = value;
  });
  keys.forEach((key: string) => {
    const value = findValueByKeyInObject(meta, key);
    if (builtPayload[key] === undefined) builtPayload[key] = value;
  });
  return sanitizeObject(builtPayload);
};
